import React, { useState } from "react";
import RoundedModal from "nvs-design-system/dist/Modals/RoundedModal";
import WhatToExpect from "./components/steps/WhatToExpect";
import VerifyAccount from "./components/steps/VerifyAccount";
import StepPagination from "./components/StepPagination";
import { TokenTransferToken } from "..";
import ReceivingAddress from "./components/steps/ReceivingAddress";
import TokenTransferForm from "./components/TokenTransferForm";
import ReviewDetails from "./components/steps/ReviewDetails";
import { AnimatePresence } from "framer-motion";
import {
  useTokenTransferModalSteps,
  TokenTransferSteps,
} from "./hooks/useTokenTransferModalSteps";
import Transferring from "./components/steps/Transferring";
import { TokenTransferResponseBody, TokenTransferStatus } from "./hooks/types";
import { useTokenTransferAnalytics } from "./hooks/analytics";

type TransferModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialStep?: TokenTransferSteps;
  token: TokenTransferToken;
  collectionSlug: string;
  swooshHandle: string;
};

export default function TokenTransferModal({
  isOpen = false,
  onClose,
  token,
  initialStep = TokenTransferSteps.WhatToExpect,
  collectionSlug,
  swooshHandle,
}: TransferModalProps) {
  const {
    currentStep,
    nextStep,
    prevStep,
    isStepShowing,
    isPaginationShowing,
    setCurrentStep,
    setStepError,
  } = useTokenTransferModalSteps(initialStep);

  const [transactionsSuccess, setTransactionSuccessData] =
    useState<TokenTransferResponseBody | null>(null);
  const [transferStatus, setTransferStatus] = useState<TokenTransferStatus>();
  const isTransferPending = transferStatus == TokenTransferStatus.Pending;

  const sendAnalytics = useTokenTransferAnalytics(
    token,
    collectionSlug,
    swooshHandle,
  );

  const onModalClose = () => {
    // since we don't allow closing modal during transfer - but this function is called when clicking outside the modal too, make sure we don't allow closing it if this executes
    if (isTransferPending) return;

    setCurrentStep(TokenTransferSteps.WhatToExpect);
    onClose();
  };

  return (
    <AnimatePresence>
      <RoundedModal
        isOpen={isOpen}
        onClose={onModalClose}
        showCloseButton={!isTransferPending}
        className="text-white laptop:!px-14 laptop:!py-12 laptop:!max-w-[625px]"
      >
        <StepPagination
          isShowing={isPaginationShowing}
          currentStep={currentStep}
          onBackClick={prevStep}
        />

        <WhatToExpect
          isShowing={isStepShowing(TokenTransferSteps.WhatToExpect)}
          onContinue={() => {
            nextStep();
            sendAnalytics.beginTransferClicked();
          }}
        />

        <VerifyAccount
          isShowing={isStepShowing(TokenTransferSteps.VerifyAccount)}
          onSuccess={async () => {
            nextStep();
            sendAnalytics.mfaCodeRedeemed();
          }}
          onError={() => setStepError(true)}
        />

        <TokenTransferForm
          onSubmit={() => {
            nextStep();
            setTransferStatus(TokenTransferStatus.Pending);
            sendAnalytics.tokenTransferClicked();
            sendAnalytics.tokenTransferPendingViewed();
          }}
          onSuccess={(data) => setTransactionSuccessData(data)}
          token={token}
        >
          <ReceivingAddress
            isShowing={isStepShowing(TokenTransferSteps.ReceivingAddress)}
            onContinue={() => {
              nextStep();
              sendAnalytics.receiverWalletAddressConfirmed();
            }}
          />

          <ReviewDetails
            isShowing={isStepShowing(TokenTransferSteps.ReviewDetails)}
            token={token}
          />

          <Transferring
            transactionId={transactionsSuccess?.transactionId}
            isShowing={isStepShowing(TokenTransferSteps.Transferring)}
            token={token}
            onContinue={onClose}
            onError={() => {
              setStepError(true);
              setTransferStatus(TokenTransferStatus.Failed);
            }}
            onSuccess={() => {
              setTransferStatus(TokenTransferStatus.Completed);
              sendAnalytics.tokenTransferConfirmationViewed();
            }}
          />
        </TokenTransferForm>
      </RoundedModal>
    </AnimatePresence>
  );
}
